import React, { useState, useEffect, useContext, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { AuthContext } from '../AuthContext';
import axios from 'axios';
import { BrowserProvider } from 'ethers'; // Add this import

// Support AI Server (Same architecture as Web3Broker)
const SUPPORT_AI_SERVER = 'https://grok-agent-proxy-web3brokerai.replit.app'; // Same proxy

const SupportCommunity = () => {
  const { isAuthenticated, ensName, loginWithSIWE, isENSHolder } = useContext(AuthContext) || {};
  const [account, setAccount] = useState(null);

  // Support AI Chat State (Same architecture as Web3Broker)
  const [chatOpen, setChatOpen] = useState(false);

  // Initialize connection
  useEffect(() => {
    const init = async () => {
      if (window.ethereum && isAuthenticated && isENSHolder) {
        try {
          const provider = new BrowserProvider(window.ethereum);
          const accounts = await provider.send("eth_requestAccounts", []);
          setAccount(accounts[0]);
        } catch (err) {
          console.error("Failed to connect to blockchain:", err);
        }
      }
    };
    init();
  }, [isAuthenticated, isENSHolder]);

  // ENS Domain Connection Handler
  const connectWallet = async () => {
    await loginWithSIWE();
  };

  // Show ENS domain connection requirement if not authenticated
  if (!isAuthenticated || !isENSHolder) {
    return (
      <div style={{ textAlign: 'center', color: 'gold', marginTop: '20px' }}>
        <p>Please mint your .xbsys.eth subdomain to access BankSystem Support.</p>
        <button
          onClick={connectWallet}
          style={{
            background: 'transparent',
            border: '2px solid gold',
            color: 'gold',
            padding: '15px 30px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
          }}
        >
          Connect Wallet
        </button>
      </div>
    );
  }

  // BankSystem Support AI Agent Component
  const SupportChatField = () => {
    const [localMessages, setLocalMessages] = useState([]);
    const [localInput, setLocalInput] = useState('');
    const [localAiLoading, setLocalAiLoading] = useState(false);
    const inputRef = useRef(null);

    // Initialize with welcome message - OPTIMIZED PROMPT
    useEffect(() => {
      if (localMessages.length === 0) {
        setLocalMessages([{
          role: 'assistant',
          displayName: 'BankSystem Support',
          content: `Welcome to BankSystem Support, ${ensName || '.xbsys.eth'}! I'm here to help with:\n\n• ENS Domain Setup & Issues\n• Platform Access & Features\n• Dbank Equity & Investments\n• Security & Technical Support\n• BankSystem Wallet (BsWallet) Questions\n• General Banking Questions\n\nHow can I assist you today?`
        }]);
      }
    }, [ensName]);

    const sendMessage = async () => {
      if (!localInput || localAiLoading) return;
      setLocalAiLoading(true);

      const userMessage = {
        role: 'user',
        displayName: ensName || '.xbsys.eth',
        content: localInput
      };
      setLocalMessages((prev) => [...prev, userMessage]);
      const userInput = localInput;
      setLocalInput('');

      try {
        const response = await axios.post(`${SUPPORT_AI_SERVER}/xai/v1/chat/completions`, {
          model: 'grok-4',
          messages: [
            {
              role: 'system',
              content: `You are BankSystem Support AI, the official customer support agent for BankSystem - the complete Web3 financial ecosystem.

CRITICAL KNOWLEDGE BASE:

BANKSYSTEM ARCHITECTURE:
• Complete Web3 banking platform with owned infrastructure
• ENS Domain: .xbsys.eth (master key for all services)
• Custom minting contract: 0x40B4d1d8bCBf4eE89005adF2c48503C53210D26C
• Max 5 domains per wallet, 3-63 characters (a-z, 0-9, hyphens)
• Primary name setup required at app.ens.domains

LIVE SERVICES:
• Web3Pay.eth - Payments infrastructure
• Web3Cash.eth - Cash management
• Web3FX.eth - Forex trading
• Web3Broker.eth - Stock brokerage + AI (@Web3BrokerAI)
• Web3Markets.eth - RWA marketplace
• SafeGuard - OmniLock.eth multi-sig escrow
• MoneyTalks - Community forums (Drupal)
• Dbank Shares - Tokenized equity (1B total, 100M public)
• BankSystem Wallet (BsWallet) - Secure Web3 wallet

BANKSYSTEM WALLET (BSWALLET) TECHNICAL SPECS:
• Framework: React.js with Ethers.js v6 integration
• Access: Available at /bswallet route (no ENS required for basic features)
• Key Features:
  - Real-time portfolio tracking with USD values
  - ETH and ERC-20 token management (USDC, USDT, DAI, WETH)
  - Transaction history with Etherscan integration
  - Secure send/receive functionality with gas optimization
  - Multi-token support with auto-detection
  - ENS name display and integration
  - Non-custodial security architecture

WALLET TECHNICAL DETAILS:
• APIs: Etherscan for transaction history & token balances
• Network: Ethereum Mainnet integration
• Security: Client-side key management, no server storage of sensitive data
• Token Support: Native ETH, Stablecoins (USDC, USDT, DAI), WETH, custom ERC-20
• Connection Methods: MetaMask, WalletConnect, ENS integration
• Real-time Features: ETH price feeds, gas price monitoring, portfolio valuation

WALLET ACCESS & USAGE:
• Route: Access via /bswallet or "Create Wallet" button in header
• No ENS Required: Basic wallet features available to all users
• Enhanced Features: Full integration with .xbsys.eth domains
• Mobile Ready: Responsive design for all devices
• Security: Non-custodial - users control private keys

SECURITY INFRASTRUCTURE:
• OmniPass.eth - Universal ENS gating
• OmniLock.eth - Multi-signature time-lock escrow
• Custom contracts for enhanced security
• BsWallet: Non-custodial design with client-side security

EQUITY & INVESTMENT:
• Dbank token: Company shares with dividends
• 1 billion total shares, 100 million public offering
• $0.45 per share, real revenue distribution
• Early access advantages for domain holders

PLATFORM VALUES:
• Lowest fees guaranteed (minimal workforce efficiency)
• Global market access for everyone
• Community-driven through MoneyTalks forums
• Transparent operations, no hidden agendas
• Ethical banking through demonstrated actions

TECHNICAL SPECIFICS:
• Network: Ethereum Mainnet for all services
• Gas estimates: Varies based on network conditions
• Contract interactions require ETH for gas
• All premium services require .xbsys.eth domain ownership

SUPPORT GUIDELINES:
1. BE TECHNICALLY ACCURATE - Use exact contract addresses and procedures
2. BE PATIENT & EDUCATIONAL - Many users are new to Web3
3. EMPHASIZE SECURITY - Never ask for private keys or seed phrases
4. PROVIDE STEP-BY-STEP - Break down complex processes
5. REFER TO DOCUMENTATION - Suggest technical manual for detailed guides
6. BE ENCOURAGING - Web3 can be intimidating for newcomers
7. STAY ON BRAND - Represent BankSystem's professional, ethical values
8. ESCALATE WHEN NEEDED - Suggest community forums for complex issues

WALLET-SPECIFIC SUPPORT:
• Help users navigate to /bswallet for wallet access
• Explain non-custodial security model
• Guide on token management and transactions
• Assist with connection issues and gas optimization
• Provide troubleshooting for transaction failures

RESPONSE STYLE:
• Professional yet approachable
• Technically precise but understandable
• Action-oriented with clear next steps
• Brand-aligned with BankSystem's values
• Supportive and patient with all skill levels

Always verify technical details and provide accurate information. If unsure, direct users to official documentation or community forums.`
            },
            ...localMessages,
            userMessage
          ]
        }, {
          headers: {
            'Authorization': 'Bearer xai-iM9Qqn1TDqdFxwmCXALXUE7ZtLJcN054JPAzarlvlQeAS5nlcz4xcAx6SuBnh9iLn2sBJeDdsy6ksSNY',
            'Content-Type': 'application/json'
          }
        });

        const aiReply = {
          role: 'assistant',
          displayName: 'BankSystem Support',
          content: response.data.choices[0].message.content
        };
        setLocalMessages((prev) => [...prev, aiReply]);
      } catch (err) {
        console.error("Support AI error:", err);
        const errorReply = {
          role: 'assistant',
          displayName: 'BankSystem Support',
          content: "I'm experiencing technical difficulties. Please try again in a moment, or visit our MoneyTalks community forums for immediate assistance."
        };
        setLocalMessages((prev) => [...prev, errorReply]);
      } finally {
        setLocalAiLoading(false);
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    };

    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        sendMessage();
      }
    };

    return (
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        width: chatOpen ? '450px' : '300px',
        height: chatOpen ? '600px' : '60px',
        background: 'var(--card-bg)',
        borderRadius: '15px',
        border: '2px solid var(--gold)',
        zIndex: 1000,
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        boxShadow: '0 4px 20px var(--gold-shadow)'
      }}>
        {/* Chat Header */}
        <div
          style={{
            padding: '15px',
            background: 'linear-gradient(135deg, var(--gold), #ffed4e)',
            color: 'var(--black)',
            fontWeight: 'bold',
            textAlign: 'center',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
          onClick={() => setChatOpen(!chatOpen)}
        >
          <span>BankSystem Support AI</span>
          <span>{chatOpen ? '−' : '+'}</span>
        </div>

        {/* Chat Messages (only when open) */}
        {chatOpen && (
          <div style={{
            flex: 1,
            height: '450px',
            overflowY: 'auto',
            padding: '15px',
            background: 'var(--black)'
          }}>
            {localMessages.map((msg, idx) => (
              <div key={idx} style={{
                marginBottom: '15px',
                padding: '10px',
                borderRadius: '10px',
                background: msg.role === 'user' ? 'var(--gold-shadow)' : 'rgba(0, 255, 136, 0.1)',
                border: `1px solid ${msg.role === 'user' ? 'var(--gold)' : '#00ff88'}`
              }}>
                <div style={{
                  fontSize: '12px',
                  color: msg.role === 'user' ? 'var(--gold)' : '#00ff88',
                  marginBottom: '5px',
                  fontWeight: 'bold'
                }}>
                  {msg.displayName}
                </div>
                <div style={{ color: 'var(--text-light)', fontSize: '14px', lineHeight: '1.4', whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Chat Input (always visible but compact when closed) */}
        <div style={{
          padding: '10px',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          gap: '8px',
          alignItems: 'center'
        }}>
          <input
            ref={inputRef}
            type="text"
            value={localInput}
            onChange={(e) => setLocalInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={chatOpen ? "Ask for support..." : "Message Support AI..."}
            style={{
              flex: 1,
              padding: '8px 12px',
              background: 'var(--card-bg)',
              color: 'var(--gold)',
              border: '1px solid var(--gold)',
              borderRadius: '20px',
              fontSize: '14px',
              minWidth: '0'
            }}
          />
          <button
            onClick={sendMessage}
            disabled={localAiLoading}
            style={{
              padding: '8px 16px',
              background: localAiLoading ? 'var(--border)' : '#00ff88',
              color: 'var(--black)',
              border: 'none',
              borderRadius: '20px',
              cursor: localAiLoading ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '12px',
              whiteSpace: 'nowrap'
            }}
          >
            {localAiLoading ? '...' : chatOpen ? 'Send' : '➤'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <section className="support-container">
      <Helmet>
        <title>BankSystem Support - Customer Assistance | BankSystem</title>
      </Helmet>

      {/* Gold Card Container - Same styling as other components */}
      <div style={{
        background: 'var(--black)',
        padding: '20px',
        borderRadius: '10px',
        boxShadow: '0 0 20px var(--gold)',
        maxWidth: '1000px',
        margin: '20px auto',
      }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{ color: 'var(--gold)', fontSize: '32px', fontWeight: 'bold', marginBottom: '10px' }}>
            BankSystem Support Center
          </h1>
          <p style={{ color: 'var(--gold)', fontSize: '16px', marginBottom: '20px' }}>
            24/7 AI-Powered Customer Support & Technical Assistance
          </p>
        </div>

        {/* ENS Connection Status */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '1rem',
          borderRadius: '5px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: isAuthenticated ? '#00FF00' : '#FF0000',
            }}></div>
            <span style={{ fontWeight: 'bold', color: isAuthenticated ? '#00FF00' : '#FF0000' }}>
              {isAuthenticated ? 'CONNECTED' : 'DISCONNECTED'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: 'var(--gold)', fontSize: '14px' }}>
              {ensName ? `${ensName}` : account ? `${account.substring(0, 6)}...${account.substring(account.length - 4)}` : 'Not connected'}
            </span>
          </div>
        </div>

        {/* Support Services Grid */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h3 style={{ color: 'var(--gold)', marginBottom: '15px', borderBottom: '1px solid var(--gold)', paddingBottom: '10px', textAlign: 'center' }}>
            How Can We Help You?
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', textAlign: 'center' }}>
              <h4 style={{ color: 'var(--gold)', margin: '0 0 10px 0' }}>🔐 ENS Domain Setup</h4>
              <p style={{ fontSize: '12px', margin: '0', color: 'var(--text-secondary)' }}>
                Minting, primary name configuration, troubleshooting
              </p>
            </div>
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', textAlign: 'center' }}>
              <h4 style={{ color: 'var(--gold)', margin: '0 0 10px 0' }}>🏦 Platform Access</h4>
              <p style={{ fontSize: '12px', margin: '0', color: 'var(--text-secondary)' }}>
                Service navigation, feature guidance, access issues
              </p>
            </div>
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', textAlign: 'center' }}>
              <h4 style={{ color: 'var(--gold)', margin: '0 0 10px 0' }}>💎 Dbank Equity</h4>
              <p style={{ fontSize: '12px', margin: '0', color: 'var(--text-secondary)' }}>
                Share purchases, dividends, investment questions
              </p>
            </div>
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', textAlign: 'center' }}>
              <h4 style={{ color: 'var(--gold)', margin: '0 0 10px 0' }}>🔧 Technical Support</h4>
              <p style={{ fontSize: '12px', margin: '0', color: 'var(--text-secondary)' }}>
                Gas fees, transactions, contract interactions
              </p>
            </div>
            {/* NEW: BankSystem Wallet Support Card */}
            <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', textAlign: 'center' }}>
              <h4 style={{ color: 'var(--gold)', margin: '0 0 10px 0' }}>💰 BankSystem Wallet</h4>
              <p style={{ fontSize: '12px', margin: '0', color: 'var(--text-secondary)' }}>
                Wallet setup, token management, transaction support
              </p>
            </div>
          </div>
        </div>

        {/* Quick Help Section */}
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '20px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h3 style={{ color: 'var(--gold)', marginBottom: '15px', borderBottom: '1px solid var(--gold)', paddingBottom: '10px', textAlign: 'center' }}>
            Quick Help Resources
          </h3>
          <div style={{ color: 'var(--text-light)', lineHeight: '1.6' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
              <div style={{ background: 'rgba(0,255,136,0.1)', padding: '15px', borderRadius: '5px', border: '1px solid #00ff88' }}>
                <h4 style={{ color: '#00ff88', margin: '0 0 10px 0' }}>ENS Domain Minting</h4>
                <p style={{ fontSize: '12px', margin: '0', color: 'var(--text-secondary)' }}>
                  Visit Web3Identity section • 3-63 character names • Max 5 per wallet • Ethereum Mainnet required
                </p>
              </div>
              <div style={{ background: 'var(--gold-shadow)', padding: '15px', borderRadius: '5px', border: '1px solid var(--gold)' }}>
                <h4 style={{ color: 'var(--gold)', margin: '0 0 10px 0' }}>Primary Name Setup</h4>
                <p style={{ fontSize: '12px', margin: '0', color: 'var(--text-secondary)' }}>
                  Go to app.ens.domains • Connect wallet • Find your domain • Set as primary name
                </p>
              </div>
              {/* NEW: BankSystem Wallet Help Card */}
              <div style={{ background: 'rgba(0,100,255,0.1)', padding: '15px', borderRadius: '5px', border: '1px solid #0064ff' }}>
                <h4 style={{ color: '#0064ff', margin: '0 0 10px 0' }}>BankSystem Wallet</h4>
                <p style={{ fontSize: '12px', margin: '0', color: 'var(--text-secondary)' }}>
                  Access via /bswallet • No ENS required • Real-time portfolio • Secure transactions
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Community Support */}
        <div style={{
          background: 'var(--gold-shadow)',
          border: '1px solid var(--gold)',
          borderRadius: '5px',
          padding: '20px',
          textAlign: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{ color: 'var(--gold)', margin: '0 0 15px 0' }}>Community Support</h3>
          <p style={{ color: 'var(--text-light)', margin: '0 0 15px 0', lineHeight: '1.6' }}>
            Join our MoneyTalks community forums for peer support, detailed discussions, and shared knowledge about BankSystem services and Web3 finance.
          </p>
          <div style={{ background: 'rgba(0,255,136,0.1)', border: '1px solid #00ff88', borderRadius: '5px', padding: '12px', marginTop: '15px' }}>
            <p style={{ color: '#00ff88', margin: '0', fontSize: '14px' }}>
              <strong>Pro Tip:</strong> The Support AI has comprehensive knowledge of all BankSystem services including the new BankSystem Wallet and can provide instant assistance for most issues.
            </p>
          </div>
        </div>
      </div>

      {/* Fixed Support Chat Field */}
      <SupportChatField />
    </section>
  );
};

export default SupportCommunity;
